import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Settings as SettingsIcon, Save, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { API_URL, getAuthHeader } from "@/config";

interface VirtualClassSettingsProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    token: string | null;
}

const VirtualClassSettings: React.FC<VirtualClassSettingsProps> = ({
    isOpen,
    onOpenChange,
    token
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [defaults, setDefaults] = useState({
        default_max_enrollment_days: 730 // Default to 2 years
    });

    useEffect(() => {
        if (isOpen && token) {
            fetchSettings();
        }
    }, [isOpen, token]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}/settings/virtual_class_defaults`, {
                headers: getAuthHeader(token),
            });
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.setting) {
                    const parsed = typeof data.setting === 'string' ? JSON.parse(data.setting) : data.setting;
                    setDefaults(prev => ({ ...prev, ...parsed }));
                }
            }
        } catch (error) {
            console.error("Failed to fetch settings", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        if (!token) return;
        setIsSaving(true);
        try {
            const response = await fetch(`${API_URL}/settings`, {
                method: 'POST',
                headers: getAuthHeader(token),
                body: JSON.stringify({
                    action: 'update_virtual_class_defaults',
                    defaults: defaults
                }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    toast.success("Paramètres mis à jour avec succès");
                    onOpenChange(false);
                } else {
                    toast.error(data.message || "Erreur lors de la mise à jour");
                }
            } else {
                toast.error("Erreur lors de la mise à jour");
            }
        } catch (error) {
            console.error("Failed to save settings", error);
            toast.error("Erreur de connexion au serveur");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[450px] rounded-3xl">
                <DialogHeader>
                    <div className="h-12 w-12 bg-slate-100 text-slate-600 rounded-2xl flex items-center justify-center mb-4">
                        <SettingsIcon className="h-6 w-6" />
                    </div>
                    <DialogTitle className="text-xl font-black">Configuration par défaut</DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Définissez les filtres appliqués automatiquement si aucune condition n'est spécifiée.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-6 space-y-6">
                    <div className="space-y-3">
                        <Label htmlFor="enrollment-days" className="text-xs font-bold text-slate-700 uppercase tracking-wider ml-1">
                            Durée d'accès après inscription (jours)
                        </Label>
                        <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input
                                id="enrollment-days"
                                type="number"
                                value={defaults.default_max_enrollment_days}
                                onChange={(e) => setDefaults({ ...defaults, default_max_enrollment_days: parseInt(e.target.value) || 0 })}
                                className="h-12 pl-12 bg-slate-50/50 border-slate-200 rounded-2xl focus:ring-primary/20 text-base"
                                placeholder="730"
                            />
                        </div>
                        <p className="text-[11px] text-slate-400 italic ml-1">
                            Par défaut, les étudiants inscrits depuis plus de 2 ans (730 jours) n'ont plus accès aux nouvelles classes.
                        </p>
                    </div>

                    <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-4">
                        <div className="h-8 w-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center shrink-0">
                            <Loader2 className="h-4 w-4 animate-spin" />
                        </div>
                        <div>
                            <p className="text-xs font-bold text-blue-900">Le saviez-vous ?</p>
                            <p className="text-[11px] text-blue-800/70 leading-relaxed mt-1">
                                Si vous définissez un filtre spécifique sur la date d'inscription lors de la création d'une classe, ce réglage par défaut sera ignoré.
                            </p>
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        className="rounded-xl font-bold h-12"
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isSaving || isLoading}
                        className="rounded-xl font-bold h-12 px-8 flex items-center gap-2"
                    >
                        {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default VirtualClassSettings;
